import { action, computed, makeObservable, observable } from 'mobx';
import { CustomField, addResizeObserver, Translate } from '@wix/bex-core';
import { BaseInputRef } from '../fields';

export interface CustomFieldsFormStateParams {
  schema: CustomField[];
  extendedFields?: Record<string, any>;
  translate: Translate;
}

export type CustomFieldsFormSize = 'default' | 'small';

export class CustomFieldsFormState {
  _schema;
  _size: CustomFieldsFormSize;
  _extendedFields;
  _initialValues;
  _translate: Translate;
  _inputRefs: { [fieldId: string]: BaseInputRef };
  _formContainerRef: { current: HTMLDivElement | null } = {
    current: null,
  };

  constructor({
    schema,
    translate,
    extendedFields = {},
  }: CustomFieldsFormStateParams) {
    this._schema = schema;
    this._extendedFields = observable.map<string, any>(extendedFields);
    this._initialValues = { ...extendedFields };
    this._inputRefs = {};
    this._size = 'default';
    this._translate = translate;

    makeObservable(this, {
      setValue: action,
      setNewSchema: action,
      setContainerRef: action,
      values: computed,
      formSize: computed,
      schema: computed,
      isDirty: computed,
      _schema: observable.ref,
      _inputRefs: observable.ref,
      _formContainerRef: observable.ref,
      _size: observable.ref,
      _adjustExtendedFields: action,
      _adjustFormSize: action.bound,
    });
  }

  _adjustFormSize() {
    if (this._formContainerRef.current) {
      const element = this._formContainerRef.current;

      if (element.clientWidth < 842) {
        this._size = 'small';
      } else {
        this._size = 'default';
      }
    }
  }

  get formSize() {
    return this._size;
  }

  get schema() {
    return this._schema;
  }

  init() {
    const disposers = [
      addResizeObserver(this._formContainerRef.current, this._adjustFormSize),
    ];

    this._adjustFormSize();

    return () => {
      disposers.forEach((d) => d?.());
    };
  }

  setValue<T>(id: string, value: T) {
    this._extendedFields.set(id, value);
  }

  getInitialValue(id: string) {
    return this._initialValues[id];
  }

  setInputRef(id: string, input: BaseInputRef) {
    this._inputRefs[id] = input;
  }

  setNewSchema(schema: CustomField[]) {
    this._schema = schema;
    this._adjustExtendedFields();
  }

  setContainerRef(ref: HTMLDivElement | null) {
    this._formContainerRef.current = ref;
  }

  _adjustExtendedFields() {
    const existingFields = this._schema.map((field) => field.id);
    this._extendedFields.forEach((_, key) => {
      if (!existingFields.includes(key)) {
        this._extendedFields.delete(key);
      }
    });
  }

  validate(): { isValid: boolean; extendedFields?: { [k: string]: any } } {
    const invalid = Object.keys(this._inputRefs).find((fieldId) => {
      const ref = this._inputRefs[fieldId];
      return ref.invalid;
    });

    if (invalid) {
      this._inputRefs[invalid].focus?.();
      return { isValid: false };
    }

    return { isValid: true, extendedFields: this.values };
  }

  get isDirty() {
    return !!Array.from(this._extendedFields.entries()).find(([key, value]) => {
      if (this._initialValues.hasOwnProperty(key)) {
        return this._initialValues[key] !== value;
      } else {
        // includes both any string value and boolean types (all existing field types values)
        return !!value;
      }
    });
  }

  get values() {
    return Object.fromEntries(this._extendedFields);
  }
}
